import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// useSubscriptionGate — PAY-001
// Chama check-subscription-status Edge Function com cache de 5min
// Fail-open: se erro → isPremium = true (não bloqueia UX)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface SubscriptionGateData {
  status: 'active' | 'trial' | 'expired' | 'none';
  plan: string;
  limits: {
    conversations_month: number;
    contacts: number;
    ai_calls_month: number;
    users: number;
    triggers: number;
  };
  usage: {
    conversations: number;
    contacts: number;
    ai_calls: number;
  };
  can_use_features: boolean;
  usage_percent: number;
  trial_days_left: number;
  current_period_end: string | null;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

// Cache global (compartilhado entre instâncias do hook)
let _cachedData: SubscriptionGateData | null = null;
let _cacheTimestamp = 0;

export function useSubscriptionGate() {
  const { session, user } = useAuth();
  const [data, setData] = useState<SubscriptionGateData | null>(_cachedData);
  const [isLoading, setIsLoading] = useState(!_cachedData);
  const [error, setError] = useState<string | null>(null);
  const fetchInProgress = useRef(false);

  const fetchStatus = useCallback(async (force = false) => {
    if (!session?.access_token || !user?.id) return;

    // Usar cache se ainda válido
    const now = Date.now();
    if (!force && _cachedData && (now - _cacheTimestamp) < CACHE_TTL_MS) {
      setData(_cachedData);
      setIsLoading(false);
      return;
    }

    // Evitar chamadas duplicadas
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    setIsLoading(true);
    try {
      const { data: responseData, error: fnError } = await supabase.functions.invoke(
        'check-subscription-status',
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (fnError) throw fnError;

      const result = responseData as SubscriptionGateData;
      _cachedData = result;
      _cacheTimestamp = Date.now();
      setData(result);
      setError(null);
    } catch (err) {
      console.error('[useSubscriptionGate] Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Fail-open: não bloqueia a experiência
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [session?.access_token, user?.id]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Derivados para facilitar uso nos componentes
  const isPremium = data ? data.can_use_features : true; // fail-open
  const isExpired = data?.status === 'expired';
  const isTrial = data?.status === 'trial';
  const isActive = data?.status === 'active';
  const usagePercent = data?.usage_percent ?? 0;
  const trialDaysLeft = data?.trial_days_left ?? 0;

  return {
    // Estado
    isPremium,
    isExpired,
    isTrial,
    isActive,
    isLoading,
    error,

    // Dados
    plan: data?.plan ?? 'free',
    limits: data?.limits ?? null,
    usage: data?.usage ?? null,
    usagePercent,
    trialDaysLeft,
    currentPeriodEnd: data?.current_period_end ?? null,

    // Ações
    refresh: () => fetchStatus(true),
  };
}
